/**
 * ============================================================================
 * 📦 Server Package Service — Ersetzt Firestore für Pakete
 * ============================================================================
 * 
 * Wird nur aktiv wenn USE_SERVER = true in serverConfig.js
 * Gleiche Funktionssignaturen wie der bestehende packageSyncHelper,
 * aber kommuniziert mit dem eigenen Server statt Firebase.
 * ============================================================================
 */

import { api } from './apiService';
import { stempelId } from './mitarbeiter';

// ============================================================
// Pakete — CRUD
// ============================================================

/**
 * Alle Pakete laden (optionaler Delta-Sync)
 * @param {string} [since] - ISO-Timestamp für Delta-Sync
 * @returns {Promise<{packages: Array, count: number, timestamp: string}>}
 */
export async function loadPackages(since) {
  const params = {};
  if (since) params.since = since;
  const result = await api.get('/packages', params);
  return result;
}

/**
 * Einzelnes Paket laden
 * @param {string} id - Paket-ID oder Firebase-ID
 */
export async function getPackage(id) {
  const pkg = await api.get(`/packages/${id}`);
  return pkg;
}

const signatureCache = new Map();

export async function loadPackageSignature(id) {
  if (!id) return null;
  const key = String(id);
  if (signatureCache.has(key)) return signatureCache.get(key);
  const pkg = await api.get(`/packages/${encodeURIComponent(key)}`);
  const sig = pkg?.abhol_unterschrift && typeof pkg.abhol_unterschrift === 'object'
    ? {
        signatureDataUrl: pkg.abhol_unterschrift.signatureDataUrl || '',
        abgeholtVonName: pkg.abhol_unterschrift.abgeholtVonName || '',
        abholUnterschriftAm: pkg.abhol_unterschrift.abholUnterschriftAm || '',
        abholArt: pkg.abhol_unterschrift.abholArt || '',
        bevollmaechtigter: pkg.abhol_unterschrift.bevollmaechtigter || null,
        vollmachtFoto: pkg.abhol_unterschrift.vollmachtFoto || '',
        ausgabeVorgangId: pkg.abhol_unterschrift.ausgabeVorgangId || '',
      }
    : null;
  if (sig && (sig.signatureDataUrl || sig.abgeholtVonName || sig.vollmachtFoto || sig.bevollmaechtigter)) {
    if (signatureCache.size >= 100) {
      const oldestKey = signatureCache.keys().next().value;
      if (oldestKey !== undefined) signatureCache.delete(oldestKey);
    }
    signatureCache.set(key, sig);
  }
  return sig;
}

export async function searchArchivePackages({ q = '', limit = 50, offset = 0 } = {}) {
  const params = { limit, offset };
  if (q && String(q).trim()) params.q = String(q).trim();
  const res = await api.get('/packages/archiv', params);
  return {
    packages: (res.packages || []).map((r) => ({ ...mapPackageFromServer(r), isArchived: true })),
    count: res.count || 0,
    hasMore: !!res.hasMore,
    offset: res.offset || 0,
    limit: res.limit || limit,
  };
}

/**
 * Neues Paket anlegen
 * @param {Object} paket - Paketdaten
 */
export async function createPackage(paket) {
  const mapped = mapPackageToServer(paket);
  return api.post('/packages', mapped);
}

/**
 * Paket aktualisieren (Upsert by Firebase-ID)
 * @param {string} id - Firebase-ID des Pakets
 * @param {Object} paket - Aktualisierte Paketdaten
 */
export async function updatePackage(id, paket) {
  const mapped = mapPackageToServer(paket);
  return api.put(`/packages/${id}`, mapped);
}

/**
 * Paket löschen (Soft-Delete)
 * @param {string} id - Paket-ID
 * @param {boolean} [hard=false] - Hartes Löschen
 */
export async function deletePackage(id, hard = false) {
  return api.delete(`/packages/${id}`, hard ? { hard: 'true' } : undefined);
}

/**
 * Batch-Upsert (mehrere Pakete auf einmal)
 * @param {Array} packages - Array von Paketen
 */
export async function batchUpsertPackages(packages) {
  const mapped = packages.map(mapPackageToServer);
  return api.post('/packages/batch', { packages: mapped });
}

/**
 * Paket-Anzahl laden
 */
export async function getPackageCount() {
  const result = await api.get('/packages/count');
  return result.count;
}

// ============================================================
// Sync — Hash-basiert (ersetzt 3-Stufen-Sync)
// ============================================================

/**
 * Sync-Hash vom Server laden
 * @returns {Promise<{packagesHash: string|null, lastSync: string|null}>}
 */
export async function getSyncHash() {
  return api.get('/user/sync-hash');
}

/**
 * Sync-Hash auf dem Server aktualisieren
 * @param {string} hash - Neuer SHA-256 Hash
 */
export async function updateSyncHash(hash) {
  return api.put('/user', {
    packagesHash: hash,
    lastSync: new Date().toISOString(),
  });
}

/**
 * Vollständiger Sync via Server:
 * 1. Hash prüfen → wenn gleich: fertig
 * 2. Delta-Sync seit letztem Timestamp
 * 3. Fallback: Alle Pakete laden
 * 
 * @param {string} localHash - Lokaler Hash aus IndexedDB
 * @param {string} [lastSyncTimestamp] - Letzter Sync-Zeitpunkt
 * @returns {Promise<{packages: Array, syncType: string, hash: string}>}
 */
export async function syncPackages(localHash, lastSyncTimestamp) {
  // Stufe 1: Hash-Check
  const serverSync = await getSyncHash();
  
  if (serverSync.packagesHash === localHash && localHash) {
    return { packages: [], syncType: 'none', hash: localHash };
  }

  // Stufe 2: Delta-Sync (wenn wir einen Timestamp haben)
  if (lastSyncTimestamp) {
    const delta = await loadPackages(lastSyncTimestamp);
    if (delta.packages.length < 100) {
      return {
        packages: delta.packages.map(mapPackageFromServer),
        syncType: 'delta',
        hash: serverSync.packagesHash,
        timestamp: delta.timestamp,
      };
    }
  }

  // Stufe 3: Full-Sync
  const full = await loadPackages();
  return {
    packages: full.packages.map(mapPackageFromServer),
    syncType: 'full',
    hash: serverSync.packagesHash,
    timestamp: full.timestamp,
  };
}

// ============================================================
// Chronik
// ============================================================

/**
 * Chronik-Einträge laden
 * @param {Object} [options] - { limit, since }
 */
export async function loadChronik(options = {}) {
  return api.get('/chronik', options);
}

/**
 * Chronik-Eintrag speichern (Upsert)
 * @param {string} id - Chronik-ID
 * @param {Object} entry - { aktion, paketId, details, timestamp }
 */
export async function saveChronikEntry(id, entry) {
  return api.put(`/chronik/${id}`, entry);
}

/**
 * Mehrere Chronik-Einträge auf einmal speichern
 * @param {Array} entries - Array von { id, aktion, paketId, details, timestamp }
 */
export async function batchSaveChronikEntries(entries) {
  return api.post('/chronik/batch', { entries });
}

// ============================================================
// Mapping: Frontend-Format ↔ Server-Format
// ============================================================

/**
 * Frontend-Paket → Server-Format (camelCase → snake_case im Server)
 */
function mapPackageToServer(pkg) {
  // 👷 Mitarbeiter-Stempel: am Paket gemerkter (offline exakt) oder der gerade Angemeldete. Der Server nimmt
  // ihn nur beim echten Übergang (neu / ausgegeben / zurückgeschickt) — beim Bearbeiten ändert sich nichts.
  const wer = stempelId();
  const erfasstVon = pkg.erfasstVon || wer;
  const ausgegebenVon = pkg.ausgegebenVon || ((pkg.ausgegeben || pkg.zurueckgeschickt) ? wer : null);
  return {
    firebaseId: pkg.id || pkg.firebaseId,
    absender: pkg.absender,
    // 🐛 FIX 25.09.2026: `name` ist das Feld, das die App bearbeitet. Vom Server geladene Pakete
    // tragen zusätzlich das ALTE `empfaenger`/`empfaengerName` — stand das vorne, ging jede
    // Umbenennung (Bearbeiten, Vinted-Import) auf dem Server still verloren.
    empfaenger: pkg.name || pkg.empfaenger || pkg.empfaengerName,
    name: pkg.name || pkg.empfaenger || pkg.empfaengerName,
    gewicht: pkg.gewicht,
    trackingNummer: pkg.trackingNumber || pkg.trackingNummer || pkg.sendungsnummer,
    status: pkg.status,
    notizen: pkg.notizen,
    timestamp: pkg.timestamp || pkg.createdAt,
    abpiepsdatum: pkg.abpiepsdatum || pkg.scanDate,
    abholdatum: pkg.abholdatum,
    imageUrl: pkg.imageUrl,
    driveFileId: pkg.driveFileId,
    hash: pkg.hash,
    deleted: pkg.deleted || false,
    // Erweiterte Felder
    ausgegeben: pkg.ausgegeben || false,
    zurueckgeschickt: pkg.zurueckgeschickt || false,
    ausgegebenAm: pkg.ausgegebenAm,
    streetAndNumber: pkg.streetAndNumber,
    postalAndCity: pkg.postalAndCity,
    regalplatz: pkg.regalplatz,
    erfasstMit: pkg.erfasstMit,
    aiCost: pkg.aiCost,
    lastUpdated: pkg.lastUpdated || Date.now(),
    updatedBy: pkg.updatedBy,
    datum: pkg.datum,
    international: pkg.international || false,
    paketFarbe: pkg.paketFarbe || null,
    paketAnzahl: pkg.paketAnzahl || 1,
    paketdienst: pkg.paketdienst || undefined, // 🚚 vom Etikett-Logo gelesen (fürs Etikett beim Nachdruck)
    ...(pkg.palettenTyp !== undefined ? { palettenTyp: pkg.palettenTyp || '' } : {}),
    // ⚠️ Schadensdoku: nur mitschicken, wenn das Paket eine hat (undefined = am Server nichts ändern, null = löschen)
    ...(pkg.schaden !== undefined ? { schaden: pkg.schaden } : {}),
    // ✍️ Abhol-Unterschrift + Name des Abholers + optional Bevollmächtigter / Vollmacht-Foto (falls vorhanden oder beim Undo geleert)
    ...(pkg.signatureDataUrl || pkg.abgeholtVonName || pkg.vollmachtFoto || pkg.bevollmaechtigter
      ? {
          unterschrift: {
            signatureDataUrl: pkg.signatureDataUrl || '',
            abgeholtVonName: pkg.abgeholtVonName || '',
            abholUnterschriftAm: pkg.abholUnterschriftAm || pkg.ausgegebenAm || new Date().toISOString(),
            ...(pkg.abholArt ? { abholArt: pkg.abholArt } : {}),
            ...(pkg.bevollmaechtigter ? { bevollmaechtigter: pkg.bevollmaechtigter } : {}),
            ...(pkg.vollmachtFoto ? { vollmachtFoto: pkg.vollmachtFoto } : {}),
            ...(pkg.ausgabeVorgangId ? { ausgabeVorgangId: pkg.ausgabeVorgangId } : {}),
          },
        }
      : pkg.unterschrift === null
      ? { unterschrift: null }
      : {}),
    ...(erfasstVon ? { erfasstVon } : {}),
    ...(ausgegebenVon ? { ausgegebenVon } : {}),
    ...(wer ? { mitarbeiter: wer } : {}), // wer gerade handelt (Teilausgabe hat keinen Ausgabe-Stempel am Paket)
  };
}

/**
 * Server-Paket → Frontend-Format
 */
function formatEmpfaengerMitGrenzpaketKd(serverPkg) {
  const rawName = String(serverPkg?.empfaenger || '').trim();
  const rawNotiz = String(serverPkg?.notizen || '').trim();
  let kd = '';
  if (rawNotiz.startsWith('{"gp":1')) {
    try {
      const obj = JSON.parse(rawNotiz);
      if (obj && obj.gp === 1 && obj.kundenNr) {
        kd = String(obj.kundenNr).replace(/\D+/g, '').trim();
      }
    } catch { /* ignore */ }
  }
  if (!kd) {
    const m = String(serverPkg?.street_and_number || '').match(/^KD\s*(\d{3,8})\b/i);
    if (m) kd = m[1];
  }
  if (!kd) return serverPkg?.empfaenger;
  if (!rawName) return `(${kd})`;
  if (rawName.includes(kd)) return rawName;
  return `${rawName} (${kd})`;
}

function mapPackageFromServer(serverPkg) {
  const empfaengerAnzeige = formatEmpfaengerMitGrenzpaketKd(serverPkg);
  return {
    id: serverPkg.firebase_id || serverPkg.id?.toString(),
    firebaseId: serverPkg.firebase_id,
    serverId: serverPkg.id,
    absender: serverPkg.absender,
    empfaenger: empfaengerAnzeige,
    empfaengerName: empfaengerAnzeige,
    name: empfaengerAnzeige,
    gewicht: serverPkg.gewicht,
    trackingNumber: serverPkg.tracking_nummer,
    sendungsnummer: serverPkg.tracking_nummer,
    status: serverPkg.status,
    notizen: serverPkg.notizen,
    timestamp: serverPkg.timestamp,
    createdAt: serverPkg.timestamp,
    abpiepsdatum: serverPkg.abpiepsdatum,
    scanDate: serverPkg.abpiepsdatum,
    abholdatum: serverPkg.abholdatum,
    imageUrl: serverPkg.image_url,
    driveFileId: serverPkg.drive_file_id,
    hash: serverPkg.hash,
    deleted: serverPkg.deleted,
    uploaded: true, // Vom Server = immer uploaded
    inCloud: true, // Existiert bereits auf dem Server
    updatedAt: serverPkg.updated_at,
    // Erweiterte Felder
    ausgegeben: serverPkg.ausgegeben || false,
    zurueckgeschickt: serverPkg.zurueckgeschickt || false,
    ausgegebenAm: serverPkg.ausgegeben_am,
    streetAndNumber: serverPkg.street_and_number,
    postalAndCity: serverPkg.postal_and_city,
    regalplatz: serverPkg.regalplatz,
    erfasstMit: serverPkg.erfasst_mit,
    aiCost: serverPkg.ai_cost,
    lastUpdated: serverPkg.last_updated,
    updatedBy: serverPkg.updated_by,
    datum: serverPkg.datum,
    international: serverPkg.international || false,
    paketFarbe: serverPkg.paket_farbe || null,
    paketAnzahl: serverPkg.paket_anzahl || 1,
    // 🚚 nur setzen, wenn der Server einen Paketdienst kennt — sonst bleibt das Paket-Objekt wie bisher
    ...(serverPkg.paketdienst ? { paketdienst: serverPkg.paketdienst } : {}),
    ...(serverPkg.paletten_typ ? { palettenTyp: serverPkg.paletten_typ } : {}),
    // ⚠️ Schadensdoku nur, wenn vorhanden (Pakete ohne bleiben exakt wie bisher)
    ...(serverPkg.schaden ? { schaden: serverPkg.schaden } : {}),
    // ✍️ Abhol-Unterschrift nur, wenn vorhanden
    ...(serverPkg.abhol_unterschrift && typeof serverPkg.abhol_unterschrift === 'object'
      ? {
          ...(serverPkg.abhol_unterschrift.signatureDataUrl ? { signatureDataUrl: serverPkg.abhol_unterschrift.signatureDataUrl } : {}),
          ...(serverPkg.abhol_unterschrift.hatBild || serverPkg.abhol_unterschrift.signatureDataUrl ? { signatureHatBild: true } : {}),
          ...(serverPkg.abhol_unterschrift.abgeholtVonName ? { abgeholtVonName: serverPkg.abhol_unterschrift.abgeholtVonName } : {}),
          ...(serverPkg.abhol_unterschrift.abholUnterschriftAm ? { abholUnterschriftAm: serverPkg.abhol_unterschrift.abholUnterschriftAm } : {}),
          ...(serverPkg.abhol_unterschrift.abholArt ? { abholArt: serverPkg.abhol_unterschrift.abholArt } : {}),
          ...(serverPkg.abhol_unterschrift.bevollmaechtigter ? { bevollmaechtigter: serverPkg.abhol_unterschrift.bevollmaechtigter } : {}),
          ...(serverPkg.abhol_unterschrift.vollmachtFoto ? { vollmachtFoto: serverPkg.abhol_unterschrift.vollmachtFoto } : {}),
          ...(serverPkg.abhol_unterschrift.hatVollmacht || serverPkg.abhol_unterschrift.vollmachtFoto ? { vollmachtHatBild: true } : {}),
          ...(serverPkg.abhol_unterschrift.ausgabeVorgangId ? { ausgabeVorgangId: serverPkg.abhol_unterschrift.ausgabeVorgangId } : {}),
        }
      : {}),
    // 👷 nur wenn gestempelt (ohne Mitarbeiter bleiben die Paket-Objekte exakt wie bisher)
    ...(serverPkg.erfasst_von ? { erfasstVon: serverPkg.erfasst_von } : {}),
    ...(serverPkg.ausgegeben_von ? { ausgegebenVon: serverPkg.ausgegeben_von } : {}),
    ...(serverPkg.isArchived ? { isArchived: true } : {}),
  };
}

/**
 * 🩹 Rohe Server-Zeilen (snake_case, numerische id, kein `name`) gehören nicht in die lokale Liste.
 * Bis 10/2026 hat die Live-Aktualisierung (SSE package:created/updated) solche Zeilen direkt eingefügt
 * → auf dem zweiten Gerät erschienen Pakete OHNE NAMEN („– Regal: N2") und blieben im Speicher hängen.
 * Wandelt sie ins App-Format um; gibt es das Paket schon richtig, fliegt die rohe Kopie raus.
 * Ohne rohe Zeilen kommt dieselbe Liste unverändert zurück.
 */
export function istRohesServerPaket(p) {
  return !!p && typeof p === 'object' && 'firebase_uid' in p && !('firebaseId' in p);
}
export function repariereRohePakete(liste) {
  if (!Array.isArray(liste) || !liste.some(istRohesServerPaket)) return liste;
  const richtig = new Set(liste.filter((p) => !istRohesServerPaket(p)).map((p) => p && p.id));
  const raus = [];
  for (const p of liste) {
    if (!istRohesServerPaket(p)) { raus.push(p); continue; }
    const m = { ...mapPackageFromServer(p), uploaded: true };
    if (!m.id || richtig.has(m.id)) continue; // gibt es schon richtig (oder unbrauchbar) → rohe Kopie weg
    richtig.add(m.id);
    raus.push(m);
  }
  return raus;
}

export { mapPackageFromServer, mapPackageToServer };
